import { zodResolver } from '@hookform/resolvers/zod'
import { Boxes, Check, Loader2 } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils'
import { useSaveCatalogModule } from '../../catalogApi'
import { MODULE_CATEGORIES, PRICING_TYPES, type CatalogModuleView } from '../../types'
import { toastError } from '../../ui'

type Props = { open: boolean; module: CatalogModuleView | null; catalog: CatalogModuleView[]; onOpenChange: (v: boolean) => void }

export function CatalogModuleFormModal({ open, onOpenChange, ...rest }: Props) {
  return open ? <ModuleForm key={rest.module?.id ?? 'new'} {...rest} onDone={() => onOpenChange(false)} /> : null
}

function ModuleForm({ module, catalog, onDone }: Omit<Props, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveCatalogModule()
  const versionLocked = !!module?.changelog.length

  const schema = z.object({
    name: z.string().trim().min(2, t('projects.catalog.form.nameRequired')).max(60),
    code: z.string().trim().regex(/^[A-Za-z0-9-]{2,16}$/, t('projects.catalog.form.codeInvalid')),
    description: z.string().trim().max(160),
    category: z.enum(MODULE_CATEGORIES),
    pricing: z.enum(PRICING_TYPES),
    price: z.string().regex(/^\d+(\.\d{1,2})?$/, t('projects.catalog.form.priceInvalid')),
    dependsOn: z.array(z.string()),
    active: z.boolean(),
    version: z.string().trim().regex(/^\d+(\.\d+){0,2}(-[0-9A-Za-z.]+)?$/, t('projects.catalog.changelog.versionInvalid')),
    setupHours: z.coerce.number<string | number>().min(0).max(999),
    trialDays: z.coerce.number<string | number>().int().min(0).max(365),
    requirementsText: z.string().max(2000),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: module?.name ?? '',
      code: module?.code ?? '',
      description: module?.description ?? '',
      category: module?.category ?? 'operations',
      pricing: module?.pricing ?? 'one_time',
      price: module?.price ?? '',
      dependsOn: module?.dependsOn ?? [],
      active: module?.active ?? true,
      version: module?.version ?? '1.0',
      setupHours: module?.setupHours ?? 2,
      trialDays: module?.trialDays ?? 0,
      requirementsText: (module?.requirements ?? []).join('\n'),
    },
  })
  const { errors } = form.formState
  const candidates = catalog.filter((m) => m.id !== module?.id)

  const onSubmit = form.handleSubmit(async ({ requirementsText, ...rest }) => {
    const input = { ...rest, requirements: requirementsText.split('\n').map((r) => r.trim()).filter(Boolean) }
    try {
      await save.mutateAsync({ id: module?.id, input })
      toast.success(t(module ? 'projects.catalog.updated' : 'projects.catalog.created'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'MODULE_NAME_TAKEN') form.setError('name', { message: t('errors.MODULE_NAME_TAKEN') })
      else if (isApiError(e) && e.code === 'MODULE_CODE_TAKEN') form.setError('code', { message: t('errors.MODULE_CODE_TAKEN') })
      else if (isApiError(e) && e.code === 'MODULE_DEPENDENCY_CYCLE') form.setError('dependsOn', { message: t('errors.MODULE_DEPENDENCY_CYCLE') })
      else toastError(e)
    }
  })
  const err = (k: keyof typeof errors) => (errors[k] ? <p className="text-xs text-destructive">{errors[k]?.message}</p> : null)

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Boxes />}
      title={t(module ? 'projects.catalog.form.editTitle' : 'projects.catalog.form.createTitle')}
      description={t('projects.catalog.form.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_9rem]">
          <div className="space-y-2">
            <Label htmlFor="cm-name">{t('projects.modules.columns.name')}</Label>
            <Input id="cm-name" aria-invalid={!!errors.name} {...form.register('name')} />
            {err('name')}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cm-code">{t('projects.catalog.fields.code')}</Label>
            <Input id="cm-code" className="font-mono uppercase" maxLength={16} aria-invalid={!!errors.code} {...form.register('code')} />
            {err('code')}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cm-desc">{t('projects.fields.description')}</Label>
          <Textarea id="cm-desc" rows={2} maxLength={160} {...form.register('description')} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="cm-cat">{t('projects.catalog.fields.category')}</Label>
            <FormSelect id="cm-cat" control={form.control} name="category" options={MODULE_CATEGORIES.map((c) => ({ value: c, label: t(`projects.moduleCategory.${c}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cm-pricing">{t('projects.modules.columns.pricing')}</Label>
            <FormSelect id="cm-pricing" control={form.control} name="pricing" options={PRICING_TYPES.map((p) => ({ value: p, label: t(`projects.pricing.${p}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cm-price">{t('projects.catalog.fields.listPrice')}</Label>
            <Controller
              control={form.control}
              name="price"
              render={({ field }) => <NumberInput id="cm-price" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" aria-invalid={!!errors.price} />}
            />
            {err('price')}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cm-version">{t('projects.catalog.fields.version')}</Label>
            <Input
              id="cm-version"
              placeholder="1.0"
              aria-invalid={!!errors.version}
              readOnly={versionLocked}
              aria-describedby={versionLocked ? 'cm-version-hint' : undefined}
              className={versionLocked ? 'bg-muted/50' : undefined}
              {...form.register('version')}
            />
            {versionLocked ? (
              <p id="cm-version-hint" className="text-[11px] text-muted-foreground">
                {t('projects.catalog.fields.versionFromChangelog')}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cm-setup">{t('projects.catalog.fields.setupHoursShort')}</Label>
            <Input id="cm-setup" type="number" min={0} step={0.5} inputMode="decimal" {...form.register('setupHours')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cm-trial">{t('projects.catalog.fields.trialDaysShort')}</Label>
            <Input id="cm-trial" type="number" min={0} max={365} inputMode="numeric" {...form.register('trialDays')} />
          </div>
        </div>
        {err('version')}
        <div className="space-y-2">
          <Label htmlFor="cm-reqs">{t('projects.catalog.detail.requirements')}</Label>
          <Textarea id="cm-reqs" rows={3} placeholder={t('projects.catalog.form.requirementsPlaceholder')} aria-describedby="cm-reqs-hint" {...form.register('requirementsText')} />
          <p id="cm-reqs-hint" className="text-xs text-muted-foreground">{t('projects.catalog.form.requirementsHint')}</p>
        </div>
        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium">{t('projects.modules.requires')}</legend>
          <p className="text-xs text-muted-foreground">{t('projects.catalog.form.dependsHint')}</p>
          <Controller
            control={form.control}
            name="dependsOn"
            render={({ field }) => (
              <div className="flex flex-wrap gap-2">
                {candidates.map((m) => {
                  const on = field.value.includes(m.id)
                  return (
                    <button
                      key={m.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => field.onChange(on ? field.value.filter((x) => x !== m.id) : [...field.value, m.id])}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        on ? 'border-primary/40 bg-primary/10 text-primary' : 'bg-card text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {on ? <Check className="size-3.5" /> : null}
                      {m.name}
                    </button>
                  )
                })}
              </div>
            )}
          />
          {err('dependsOn')}
        </fieldset>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-primary)]" {...form.register('active')} />
          <span>
            {t('projects.catalog.fields.active')}
            <span className="block text-xs text-muted-foreground">{t('projects.catalog.form.activeHint')}</span>
          </span>
        </label>
      </div>
    </Modal>
  )
}
